import { useState, useContext, useEffect, useCallback } from "react";
import { axiosPrivate } from "../../../api/axios";
import Cookies from "universal-cookie";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import { Table } from "react-bootstrap";
import LoadingBar from "react-top-loading-bar";
const cookies = new Cookies();

export default function NotificationSettings() {
  const [progress, setProgress] = useState(0);
  const { state } = useContext(GlobalContext);
  const { user } = state;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const hasUserData = user && user.role;
  const { role, is_allow_self_notifications, is_allow_staff_notifications } =
    hasUserData
      ? user
      : {
          role: null,
          is_allow_self_notifications: false,
          is_allow_staff_notifications: false,
        };

  const [allowSelfNotification, setAllowSelfNotification] = useState(false);
  const [allowStaffNotification, setAllowStaffNotification] = useState(false);
  const [staffData, setStaffData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [initialStates, setInitialStates] = useState({
    selfNotify: false,
    staffNotify: false,
    staffUsers: {},
  });

  const hasStaffData = staffData.length > 0 && role === "Admin";

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      setProgress(30);
      setAllowSelfNotification(is_allow_self_notifications);
      setAllowStaffNotification(is_allow_staff_notifications);
      setInitialStates((prev) => ({
        ...prev,
        selfNotify: is_allow_self_notifications,
        staffNotify: is_allow_staff_notifications,
      }));
      setProgress(50);

      if (role === "Admin") {
        const response = await axios.get(`lender/admin/list/staff-users/`, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${cookies.get("access")}`,
          },
          withCredentials: true,
        });

        const fetchedUsers = response?.data?.staff_users || [];
        setStaffData(fetchedUsers);

        const staffUsersState = {};
        fetchedUsers.forEach((user) => {
          staffUsersState[user.email] = user.is_allow_admin_notifications;
        });

        setSelectedUsers(staffUsersState);
        setInitialStates((prev) => ({
          ...prev,
          staffUsers: staffUsersState,
        }));
        setProgress(80);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      showSnackbar(
        err?.response?.data?.message || "Failed to load data",
        "error"
      );
    } finally {
      setProgress(100);
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [is_allow_self_notifications, is_allow_staff_notifications, role]);

  useEffect(() => {
    if (hasUserData) {
      fetchAllData();
    }
  }, [hasUserData, fetchAllData]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSelfNotificationToggle = () => {
    const newValue = !allowSelfNotification;
    setAllowSelfNotification(newValue);
  };

  const handleStaffNotificationToggle = () => {
    const newValue = !allowStaffNotification;
    setAllowStaffNotification(newValue);
  };

  const handleUserToggle = (email) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [email]: !prev[email],
    }));
  };

  const hasChanges = useCallback(() => {
    if (allowSelfNotification !== initialStates.selfNotify) return true;
    if (role === "Admin") {
      if (allowStaffNotification !== initialStates.staffNotify) return true;
      for (const email in selectedUsers) {
        if (selectedUsers[email] !== initialStates.staffUsers[email]) {
          return true;
        }
      }
    }
    return false;
  }, [
    allowSelfNotification,
    allowStaffNotification,
    selectedUsers,
    initialStates,
    role,
  ]);

  const updateNotificationSettings = useCallback(async () => {
    try {
      setIsSaving(true);
      setProgress(30);

      const payload = {
        is_allow_self_notifications: allowSelfNotification,
        is_allow_staff_notifications: allowStaffNotification,
        ...(hasStaffData && {
          staff_users: staffData.map((user) => ({
            email: user.email,
            is_allow_admin_notifications:
              selectedUsers[user.email] ?? user.is_allow_admin_notifications,
          })),
        }),
      };

      await axiosPrivate.put("auth/notifications/", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookies.get("access")}`,
        },
        withCredentials: true,
      });

      setInitialStates({
        selfNotify: allowSelfNotification,
        staffNotify: allowStaffNotification,
        staffUsers: { ...selectedUsers },
      });
      setProgress(100);

      showSnackbar("Notification settings updated successfully");
    } catch (err) {
      console.error("Update failed:", err);
      showSnackbar(
        err?.response?.data?.detail || "Failed to update settings",
        "error"
      );
      setAllowSelfNotification(initialStates.selfNotify);
      setAllowStaffNotification(initialStates.staffNotify);
      setSelectedUsers(initialStates.staffUsers);
    } finally {
      setIsSaving(false);
      setTimeout(() => setProgress(0), 500);
    }
  }, [
    allowSelfNotification,
    allowStaffNotification,
    staffData,
    selectedUsers,
    hasStaffData,
    initialStates,
  ]);

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <LoadingBar
        color="rgba(0, 148, 255, 1)"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />
      {isLoading ? (
        <div style={{ height: "100vh" }}></div>
      ) : (
        <section className="min-vh-100">
          <div className="container-fluid mt-4 mb-4">
            <div className="rounded-4 row">
              <div className="col-lg-12 col-md-12">
                <div
                  className="bg-white p-4 rounded-4"
                  style={{ background: "#e2ebf7" }}
                >
                  <h4 className="py-3">Notification Settings</h4>

                  <div
                    className={`py-4 ${
                      role === "Admin" ? " border-bottom" : ""
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h3 className="h5 mb-2">Enable Self Notification</h3>
                        <p className="text-muted mb-0">
                          Receive notifications directly from the application
                          for important updates.
                        </p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input toggle-button"
                          type="checkbox"
                          checked={allowSelfNotification}
                          onChange={handleSelfNotificationToggle}
                          disabled={isSaving}
                          style={{ width: "3rem", height: "1.5rem" }}
                        />
                      </div>
                    </div>
                  </div>

                  {hasStaffData && (
                    <div className="py-4">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h3 className="h5 mb-2">Notification from Staff</h3>
                          <p className="text-muted mb-0">
                            Get alerts and updates sent by the staff users.
                          </p>
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input toggle-button"
                            type="checkbox"
                            style={{ width: "3rem", height: "1.5rem" }}
                            checked={allowStaffNotification}
                            onChange={handleStaffNotificationToggle}
                            disabled={isSaving}
                          />
                        </div>
                      </div>
                      <div
                        className="table-responsive"
                        style={{ overflowX: "auto" }}
                      >
                        <Table
                          className="table-backgorund"
                          style={{ minWidth: "600px" }}
                        >
                          <thead className="table-head">
                            <tr>
                              <th>Email</th>
                              <th>Staff Users</th>
                              <th>Allow Notifications</th>
                            </tr>
                          </thead>
                          <tbody>
                            {staffData?.map((user) => (
                              <tr key={user.email}>
                                <td>{user.email}</td>
                                <td>{user.username}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ cursor: "pointer" }}
                                    checked={
                                      selectedUsers[user.email] ??
                                      user.is_allow_admin_notifications
                                    }
                                    onChange={() =>
                                      handleUserToggle(user.email)
                                    }
                                    disabled={isSaving}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                  {hasChanges() && (
                    <button
                      className="btn btn-dark mt-3"
                      onClick={updateNotificationSettings}
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
